import {useReducedMotion} from "framer-motion";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
	SLIDE_OUT_CARDS_TUCKED, SLIDE_OUT_CARD_ORDER, slideOutCardTops, slideOutIsOpen, slideOutOpen,
	slideOutSelfOpen, slideOutStackedSpots, slideOutTuck, slideOutWithdraw, type SlideOutCardId,
	type SlideOutCardsState, type SlideOutStackedSpot,
} from "./slideOutCards";
import {AUTO_OPEN_DELAY_MS, LIFT_LEAD_MS, SLIDE_MS} from "./SlideOutCard.styles";

export interface SlideOutCardControls {
	open: boolean;
	/** The panel is lifted for the card's arrival; the frame reads it as `data-peel-card-lift`. */
	lifting: boolean;
	/** Where this card sits beside the panel on a wide screen: from the anchor down. */
	offsetTop: number;
	/** Where it sits under the panel in the stacked layout. */
	stacked: SlideOutStackedSpot;
	/** The card reports the height it draws, so the stack is spaced by what is on screen. */
	onHeight: (height: number) => void;
	openCard: () => void;
	closeCard: () => void;
}

/** A card that is not in the stack this render (not eligible) has no place to take; it renders nothing. */
const UNPLACED: SlideOutStackedSpot = {top: 0, left: null};

export interface SlideOutCardsInput {
	/** Each card has something to say right now. */
	offer: boolean;
	limit: boolean;
	upsell: boolean;
	/** A card just came OUT — the moment a family records as shown, since a tucked chip shows no offer. */
	onOpened?: (id: SlideOutCardId) => void;
}

/**
 * Thin adapter for the send screen's slide-out card family (ui#2468, ui#2278): it holds the React-free
 * bookkeeping from `./slideOutCards`, the timers of the approved entrance, the measured stack, and
 * nothing else. It lives in the panel, above the portalled cards, so a remount of a card cannot reset
 * it — see `slideOutCards.ts` for the defect that rule closes.
 *
 * The entrance is the prototype's "lift": the panel rises first, the card slides {@link LIFT_LEAD_MS}
 * later, and the panel settles once the slide is over. Under reduced motion both take their resting
 * states at once.
 *
 * <b>One card at a time.</b> The first card in `SLIDE_OUT_CARD_ORDER` that is eligible and still owed a
 * self-opening takes it; the next one waits until nothing is out, so the upsell ask never slides over
 * the warning about a cap the sender is already spending, and neither slides over the emailed offer
 * the sender came for.
 */
export function useSlideOutCards({offer, limit, upsell, onOpened}: SlideOutCardsInput): Record<SlideOutCardId, SlideOutCardControls> {
	const reduceMotion = useReducedMotion() !== false;
	const [state, setState] = useState<SlideOutCardsState>(SLIDE_OUT_CARDS_TUCKED);
	const [lifting, setLifting] = useState(false);
	const [heights, setHeights] = useState<Record<SlideOutCardId, number>>({offer: 0, limit: 0, upsell: 0});
	const timers = useRef<number[]>([]);
	const clearTimers = () => {
		for (const timer of timers.current) window.clearTimeout(timer);
		timers.current = [];
	};
	const later = (callback: () => void, delay: number) => {
		const timer = window.setTimeout(callback, delay);
		timers.current.push(timer);
		return timer;
	};
	useEffect(() => clearTimers, []);

	const arrive = (transition: (state: SlideOutCardsState) => SlideOutCardsState) => {
		if (reduceMotion) { setState(transition); return; }
		setLifting(true);
		later(() => setState(transition), LIFT_LEAD_MS);
		later(() => setLifting(false), SLIDE_MS + 120);
	};

	const eligible: Record<SlideOutCardId, boolean> = useMemo(() => ({offer, limit, upsell}), [offer, limit, upsell]);

	// On first eligibility a card peeks, then opens once by itself. The bookkeeping decides whether that
	// opening is still owed; this effect only schedules it for the FIRST card in the family order that is
	// owed one, and cancels the schedule when eligibility goes away or the reader gets there first. It
	// re-runs when the open card changes, which is how a card held back behind another gets its turn.
	// `spent` is safe as a dependency: the transitions hand back the SAME array whenever nothing was
	// added to it, so this re-runs when a self-opening is actually spent and not on every render.
	const {open: openCardId, spent} = state;
	// The open card has left the stack (ui#3197: the offer once this visit paid, the limit warning when a
	// same-plan offer takes its place). Withdrawn here, from the eligibility the panel hands down, because
	// nothing else clears `open`: the card's own × is gone with it, and a family that still believed a
	// card was out would hold every later self-opening and keep the beside-panel offer yielding for good.
	useEffect(() => {
		if (openCardId === null || eligible[openCardId]) return;
		clearTimers();
		setLifting(false);
		setState(current => slideOutWithdraw(current, openCardId));
	}, [eligible, openCardId]);
	useEffect(() => {
		if (openCardId !== null) return;
		const owed = SLIDE_OUT_CARD_ORDER.find(id => eligible[id] && !spent.includes(id));
		if (owed === undefined) return;
		const timer = later(() => arrive(current => slideOutSelfOpen(current, owed)), AUTO_OPEN_DELAY_MS);
		return () => window.clearTimeout(timer);
	}, [eligible, spent, openCardId]);

	// Reported from an effect rather than inside the state transition: React may invoke an updater more
	// than once, and a record of "this sender was shown the card" must be written once per opening.
	const openedRef = useRef<(id: SlideOutCardId) => void>(() => {});
	openedRef.current = onOpened ?? (() => {});
	const lastReported = useRef<SlideOutCardId | null>(null);
	useEffect(() => {
		if (openCardId === null) { lastReported.current = null; return; }
		if (lastReported.current === openCardId) return;
		lastReported.current = openCardId;
		openedRef.current(openCardId);
	}, [openCardId]);

	const onOfferHeight = useCallback((height: number) =>
		setHeights(prev => prev.offer === height ? prev : {...prev, offer: height}), []);
	const onLimitHeight = useCallback((height: number) =>
		setHeights(prev => prev.limit === height ? prev : {...prev, limit: height}), []);
	const onUpsellHeight = useCallback((height: number) =>
		setHeights(prev => prev.upsell === height ? prev : {...prev, upsell: height}), []);

	// Where each card sits: the wide layout's column beside the panel and the stacked layout's place under
	// it, both from `./slideOutCards`. The styles pick one per width; both are computed so a resize needs
	// no re-render to find its place.
	const placement = useMemo(() => {
		const visible = SLIDE_OUT_CARD_ORDER.filter(id => eligible[id]);
		const boxes = visible.map(id => ({id, open: slideOutIsOpen(state, id), height: heights[id]}));
		const tops = slideOutCardTops(boxes);
		const stacked = slideOutStackedSpots(boxes);
		return Object.fromEntries(visible.map((id, index) => [id, {top: tops[index], stacked: stacked[index]}])) as
			Partial<Record<SlideOutCardId, {top: number; stacked: SlideOutStackedSpot}>>;
	}, [eligible, state, heights]);

	const controlsFor = (id: SlideOutCardId, onHeight: (height: number) => void): SlideOutCardControls => ({
		open: slideOutIsOpen(state, id),
		lifting,
		offsetTop: placement[id]?.top ?? 0,
		stacked: placement[id]?.stacked ?? UNPLACED,
		onHeight,
		openCard: () => arrive(current => slideOutOpen(current, id)),
		closeCard: () => { clearTimers(); setLifting(false); setState(current => slideOutTuck(current, id)); },
	});

	return {
		offer: controlsFor("offer", onOfferHeight),
		limit: controlsFor("limit", onLimitHeight),
		upsell: controlsFor("upsell", onUpsellHeight),
	};
}
